// IMPORTANT:
// Errors like
//   Property 'hasOwn' does not exist on type 'ObjectConstructor'. Do you need to change your target library? Try changing the 'lib' compiler option to 'es2022' or later.ts(2550)
// mean yo need to run ./scripts/build.sh <your target>, there is no tsconfig.json commited into the repo so you need to run this script to copy over the right one for you
import React, { useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useResultJSON, useResultJSONBucketBaseURL } from './ResultJSON'; // Assuming this is your custom hook to fetch data
import { lslChunk, lslMessage, IlslMessage } from '../lib/lslLayer'; // run ./scripts/lib-build.sh form the component bas dir if this is underlined
import { Ig2pMessage } from '../lib/gameParent'; // run ./scripts/lib-build.sh form the component bas dir if this is underlined
import { Api } from '../api/api';
import { p2gMessage } from '../lib/gameParent';
import { Domains, IIndexable } from '../Utils';
import { Constants } from '../Constants';
import { MGame, MGames, isIGames } from '../model/Games';

interface FIPOKGameProps {
	section: string;
	symbolicId: string;
}

function getIFrameSize(a_gameCategory: string): { width: string, height: string}
{
	switch(a_gameCategory) {
		case Constants.GCAT_COGNITIVE:
			return { "width": "80vw", "height": "46.8vw" }; // 1024:600px ratio for cognitive godot games;
		case Constants.GCAT_COORDINATION:
			return { "width": "1030px", "height": "772px" }; // fixed for camera pose games
		default:
			return { "width": "80vw", "height": "46.8vw" }; // 1024:600px ratio for cognitive godot games
	}
};

export const FIPOKGame: React.FC<FIPOKGameProps> = ({ section, symbolicId }) => {
	const navigate = useNavigate();
	const auth0Obj = useAuth0();
	const jsonData = useResultJSON(); // Assuming this hook fetches JSON data from the backend
	const bucketBaseURL = useResultJSONBucketBaseURL();

	const [game, setGame] = useState<MGame>(new MGame({}, ""));
		
	useEffect(() => {
		const FLUSH_LIMIT = 1000;
		let currentBuffers: IIndexable<lslChunk> = {};
		let currentChunkNos: IIndexable<number> = {};
		let api: Api | null = null;  // URL needs the protocol, cannot be relative
		const sessionId = `${(new Date()).getTime()}-${Math.floor(Math.random() * 8999 + 1000)}`;

		const putServer = async (a_streamName: string, a_chunkNo: number, a_buffer: Uint8Array): Promise<void> => {
			try {
				if (api === null) {
					try {
						const token = await auth0Obj.getAccessTokenSilently();
						api = new Api(Domains.apiURL, token);
					} catch (e) {
						console.log(`failed to get access token: ${e}`);
						return;
					}
				}
				const response = await api.lslOutlet({
					sessionId: sessionId,
					streamName: a_streamName,
					chunkNo: a_chunkNo,
					binaryData: new Blob([a_buffer])
				});
				/*
				 * NOTE:
				 * all the stuff below is basically bloat unless we call this with await
				 * what we will do eventually (definitely not now), and add some
				 * further buffering and repeating on failure, but all that logic is
				 * "V2.0" or "V3.0" not for this "V0.1" state we are still in
				 * I kept it anyway, hopefully will cause no harm
				 */
				// Check if the response status indicates success (e.g., 2xx status codes)
				if (response.ok) {
					// Handle the successful response here
					return;
					// the reply body will be empty, just a 200 HTTP status will be returned to keep traffic at minimum
					// what could we return here anyway?
					// I did not look this up but response.json() may even throw here, in any case it is superflous
					//return response.json(); // Assuming the response is JSON
				} else {
					// Handle non-successful responses (e.g., 4xx or 5xx status codes)
					throw new Error(`Request failed with status ${response.status}`);
				}
			} catch (error) {
				// Handle fetch errors here
				console.error("Fetch error:", error);
				// You can throw or return an error object if needed
				// throw error;
			}
		}

		const flushIfNeeded = async (a_streamName?: string): Promise<void> => {
			if (a_streamName === undefined || a_streamName.length === 0) {
				let promises: Promise<void>[] = []; 
				for (let s in currentBuffers) {
					if (!(s in currentChunkNos)) {
						// uh, oh? should be there
						console.error(`stream name '${s}' is present in currentBuffers but not in currentChunkNos, fix this!`);
						continue;
					}
					promises.push(putServer(s, currentChunkNos[s]++, lslChunk.encode(currentBuffers[s]).finish()));
				}
				await Promise.all(promises);
				return;
			}
			if (!(a_streamName in currentBuffers)) {
				// uh, oh? should be there
				console.error(`flushIfNeeded is called with stream name '${a_streamName}' which is not present in currentBuffers, fix this!`);
				return;
			}
			if(currentBuffers[a_streamName].frames.length < FLUSH_LIMIT) {
				return;
			}
			if (!(a_streamName in currentChunkNos)) {
				// uh, oh? should be there
				console.error(`flushIfNeeded is called with stream name '${a_streamName}' which is present in currentBuffers but not in currentChunkNos, fix this!`);
				return;
			}
			await putServer(a_streamName, currentChunkNos[a_streamName]++, lslChunk.encode(currentBuffers[a_streamName]).finish());
		}

		const dispatchPostMessage = (a_event: Event) => {
			try {
				const evnt = a_event as MessageEvent;
				const envt_payload = evnt.data;
				if (lslMessage.verify(envt_payload) === null) {
					return handleLSLEvent(a_event);
				}
				if (p2gMessage.verify(envt_payload) === null) {
					return handleG2PMessage(a_event);
				}
			} catch (e) {
				// should never reach this
				console.warn(`something went horribly wrong in the post message handler: '${e}'`)
			}
		}
		
		const handleG2PMessage = (a_event: Event) => {
			try {
				const evnt = a_event as MessageEvent;
				const envt_payload = evnt.data;
				const g2pMsg = envt_payload as Ig2pMessage;

				if (g2pMsg.kind === "ready") {
					//post a p2gMessage back to the IFrame with the config file as text here
				}
			} catch (e) {
				// uh, oh? the unthinkable happened
				console.warn(`Ooops! received object is not an Ig2pMessage object: '${JSON.stringify(a_event)}'`)
			}
		}

		const handleLSLEvent = (a_event: Event) => {
			try {
				const evnt = a_event as MessageEvent;
				const envt_payload = evnt.data;
				const lslMsg = envt_payload as IlslMessage;

				if (lslMsg.streamName.length === 0) {
					// this is an error, no empty stream names, please
					console.warn(`LSL push received with empty stream name, fix this!`);
					return;
				}
				if (!(lslMsg.streamName in currentBuffers)) {
					currentBuffers[lslMsg.streamName] = new lslChunk();
				}
				if (!(lslMsg.streamName in currentChunkNos)) {
					currentChunkNos[lslMsg.streamName] = 0;
				}
				currentBuffers[lslMsg.streamName].frames.push(lslMsg.payload);
				if(lslMsg.payload.event !== undefined && typeof lslMsg.payload.event === "string" && lslMsg.payload.event === "end_game") {
					flushIfNeeded();
				} else {
					flushIfNeeded(lslMsg.streamName);
				}
			} catch (e) {
				console.warn(`Ooops! received object is not an IlslMessage object: '${JSON.stringify(a_event)}'`)
			}
		};

		window.addEventListener('message', dispatchPostMessage, false);
		// Cleanup function
		// This part acts like componentWillUnmount
		return () => {
			window.removeEventListener('message', dispatchPostMessage, false);
		};
		// no dependencies, on purpose
		// the reason the postMessage() handler fired multiple times was that the listener was added
		// whenever the useEffect hook's dependencies change plus at mount
		// it is important to have this logic in it's own separate useEffect() hook which has no
		// dependencies, so is guaranteed to run only once, after mount 
	});
	useEffect(() => {

		// Assuming that the "jsonData" object has been fetched using your useResultJSON hook
		// and it contains the "section" in the required format
		if (jsonData == null) {
			return;
		};

		if (isIGames(jsonData)) {
			const games = new MGames(jsonData, bucketBaseURL);
			const game = games.bySymbolicId(symbolicId);
			if (game !== undefined) {
				setGame(game);
			} else {
				console.log(`Ooops! no game with symbolicId '${symbolicId}' found!`)
			}

		}

		/*
		function simple_cb(event: Event) {
			console.log("event type: ", event.type, "; details: ", event);
		};
		*/
		/*
		const regexp = new RegExp(/\$\{bucketBaseURL\}/g);
		if (Object.hasOwn(jsonData, section)
			&& Object.hasOwn(jsonData[section], "categoryName")
			&& typeof jsonData[section].categoryName == "object"
			&& Object.hasOwn(jsonData[section], "iconURL")
			&& typeof jsonData[section].iconURL == "string"
			&& Object.hasOwn(jsonData[section], "games")
			&& Array.isArray(jsonData[section].games)) {
			let gamesObj: FIPOKGamesObject = {
				categoryName: {},
				iconURL: jsonData[section].iconURL.replace(regexp, bucketBaseURL),
				games: []
			};
			for (let lC in jsonData[section].categoryName) {
				const c = jsonData[section].categoryName[lC];
				if (typeof c == "string") {
					gamesObj.categoryName[lC] = jsonData[section].categoryName[lC];
				}
			}
			for (let g of jsonData[section].games) {
				if (Object.hasOwn(g, "title")
					&& typeof g.title == "object"
					&& Object.hasOwn(g, "symbolicId")
					&& typeof g.symbolicId == "string"
					&& Object.hasOwn(g, "iconURL")
					&& typeof g.iconURL == "string"
					&& Object.hasOwn(g, "frameURL")
					&& typeof g.frameURL == "string"
					&& g.symbolicId === symbolicId) {
					let gObj: FIPOKGameObject = {
						title: {},
						symbolicId: g.symbolicId,
						iconURL: g.iconURL.replace(regexp, bucketBaseURL),
						frameURL: g.frameURL.replace(regexp, bucketBaseURL),
						category: section
					};
					for (let lC in g.title) {
						const c = g.title[lC];
						if (typeof c == "string") {
							gObj.title[lC] = g.title[lC];
						}
					}
					setGame(gObj);
					break;
				}
			}
		}
		*/
	},
	[
		bucketBaseURL,
		jsonData,
		section,
		symbolicId
	]);

	const handleBackClick = () => {
		navigate(`/games`);
	};

	return (
		<Box display={"flex"} flexDirection={"column"} width={"100%"} height={"100%"}>
			<div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', padding: '16px' }}>
				<span onClick={handleBackClick}><u>Visszalépés</u></span>
			</div>
			<Box display={"flex"} justifyContent={"center"}>
				<Box display={"flex"} flexDirection={"row"}
					height={getIFrameSize(game.category).height}
					width={getIFrameSize(game.category).width}
					gap={"24px"}
					padding={"4px"}
					maxHeight={"600px"}
					maxWidth={"1024px"}>
 					{/* {game.title.hu} */}
					{game.category === Constants.GCAT_COGNITIVE && (
						<iframe
							src={game.frameURL}
							title={game.title.hu}
							id="gameFrame" 
							style={{ flex: 1, width: "100%", border: 'none', overflow: "hidden", boxSizing: 'border-box' }}
						/>
					)}
					{game.category === Constants.GCAT_COORDINATION && (
						<div className="iframe-container" style={{ position: 'relative', height: '768px', width: '1024px' }}>
							<iframe
								src={bucketBaseURL + "/pose_live/pose_live.html"}
								title={"pose_live"}
								style={{ overflow: 'hidden', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
								allow="camera"
							// allow="camera https://games.dev.fipok.com https://games.fipok.com" // may specify domains as well
							// see https://github.com/w3c/webappsec-permissions-policy/blob/main/permissions-policy-explainer.md#example-7---iframe-navigations
							/>
							<iframe
								src={game.frameURL}
								title={game.title.hu}
								id="gameFrame"
								style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.2)' }}
							/>
						</div>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default FIPOKGame;
